import { createWebSocketAuthLifecycle } from './websocketAuthLifecycle';

export const MEDIA_SOCKET_AUTH_RETRY_DELAY = 1000;
export const MEDIA_SOCKET_MAX_AUTH_ATTEMPTS = 5;
export const MEDIA_SOCKET_BASE_RECONNECT_DELAY = 2000;
export const MEDIA_SOCKET_MAX_RECONNECT_DELAY = 30000;

const getCanonicalUserId = backendService => {
  const user = backendService.getUser ? backendService.getUser() : null;
  return user && (user._id || user.user_id || user.id);
};

/**
 * Own one authenticated Media WebSocket without replacing the app-level socket.
 * Socket messages are not trusted until the server acknowledges authentication;
 * their payload identity is never used to choose the authenticated owner.
 */
export const connectAuthenticatedMediaSocket = ({
  url,
  backendService,
  onAuthenticated,
  onMessage,
  WebSocketImpl = WebSocket,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
  authRetryDelay = MEDIA_SOCKET_AUTH_RETRY_DELAY,
  maxAuthAttempts = MEDIA_SOCKET_MAX_AUTH_ATTEMPTS,
  baseReconnectDelay = MEDIA_SOCKET_BASE_RECONNECT_DELAY,
  maxReconnectDelay = MEDIA_SOCKET_MAX_RECONNECT_DELAY,
}) => {
  const authLifecycle = createWebSocketAuthLifecycle({
    baseDelay: baseReconnectDelay,
    maxDelay: maxReconnectDelay,
  });
  const openState = WebSocketImpl.OPEN ?? 1;
  let disposed = false;
  let socket = null;
  let authenticatedSocket = null;
  let authRetryTimer = null;
  let reconnectTimer = null;
  let authAttempts = 0;

  const clearAuthRetry = () => {
    if (authRetryTimer !== null) clearTimer(authRetryTimer);
    authRetryTimer = null;
  };

  const clearReconnect = () => {
    if (reconnectTimer !== null) clearTimer(reconnectTimer);
    reconnectTimer = null;
  };

  const isCurrentAndOpen = candidate => (
    !disposed && candidate === socket && candidate.readyState === openState
  );

  const scheduleAuthentication = candidate => {
    if (!isCurrentAndOpen(candidate) || authAttempts >= maxAuthAttempts) return;
    clearAuthRetry();
    authRetryTimer = setTimer(() => {
      authRetryTimer = null;
      authenticate(candidate);
    }, authRetryDelay);
  };

  const authenticate = async candidate => {
    if (!isCurrentAndOpen(candidate) || authenticatedSocket === candidate) return;
    authAttempts += 1;
    let userId = null;
    let token = null;
    try {
      userId = getCanonicalUserId(backendService);
      token = backendService.getToken
        ? await backendService.getToken({ skipCache: true })
        : null;
    } catch (_) {
      // A session may still be resolving. The bounded retry below handles it.
    }
    if (!isCurrentAndOpen(candidate) || authenticatedSocket === candidate) return;
    if (!userId || !token) {
      scheduleAuthentication(candidate);
      return;
    }
    candidate.send(JSON.stringify({ type: 'auth', user_id: userId, token }));
  };

  const scheduleReconnect = () => {
    if (disposed || !authLifecycle.canReconnect()) return;
    clearReconnect();
    reconnectTimer = setTimer(() => {
      reconnectTimer = null;
      connect();
    }, authLifecycle.closed());
  };

  const connect = () => {
    if (disposed) return;
    clearAuthRetry();
    authAttempts = 0;
    let candidate;
    try {
      candidate = new WebSocketImpl(url);
    } catch (_) {
      scheduleReconnect();
      return;
    }
    socket = candidate;

    candidate.onopen = () => {
      if (!isCurrentAndOpen(candidate)) return;
      authenticate(candidate);
    };

    candidate.onmessage = event => {
      if (disposed || candidate !== socket) return;
      let message;
      try {
        message = JSON.parse(event.data);
      } catch (_) {
        return;
      }
      if (message.type === 'authenticated') {
        if (authenticatedSocket === candidate) return;
        authenticatedSocket = candidate;
        clearAuthRetry();
        authLifecycle.authenticated();
        onAuthenticated();
        return;
      }
      if (authenticatedSocket === candidate) onMessage(message);
    };

    candidate.onclose = event => {
      if (candidate !== socket) return;
      clearAuthRetry();
      authenticatedSocket = null;
      if (disposed) return;
      if (event?.code === 1008) {
        authLifecycle.authenticationRejected();
        clearReconnect();
        return;
      }
      scheduleReconnect();
    };
  };

  connect();

  return {
    close() {
      if (disposed) return;
      disposed = true;
      clearAuthRetry();
      clearReconnect();
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.close();
      }
      authenticatedSocket = null;
      socket = null;
    },
  };
};
